import {
  AnomalyDirection,
  ContributorType,
} from '../../../../../../../shared/models/anomaly-detection/models/AnomalyIncident';
import { AnomalyMetricKey, AnomalyMetricSemantics } from '../../../../../../../shared/models/anomaly-detection/models/AnomalyIncidentsResponse';
import {
  getAnomalyModelLabel,
  sortAnomalyModels,
  sortAnomalyPrompts,
} from '../../../../../../../shared/helpers/AnomalyIncidentHelpers';
import { MarkerMetricIncident } from './anomalyMetricConfig';

const MAX_MERGED_PROMPTS = 5;
const MAX_MERGED_MODELS = 3;

export type MergedContributorImpact = {
  metric: AnomalyMetricKey;
  type: ContributorType;
  value: number;
  direction: AnomalyDirection;
  unit: string;
};

export type MergedContributor = {
  key: string;
  label: string;
  impacts: MergedContributorImpact[];
};

const getContributorScore = (impacts: MergedContributorImpact[]): number => {
  if (!impacts.length) return 0;
  return Math.max(...impacts.map((impact) => Math.abs(impact.value)));
};

const sortAndLimit = (contributors: MergedContributor[], limit: number): MergedContributor[] => {
  return [...contributors]
    .sort((left, right) => getContributorScore(right.impacts) - getContributorScore(left.impacts))
    .slice(0, limit);
};

const buildPromptMap = (
  metricIncidents: MarkerMetricIncident[],
  metricSemantics: Record<AnomalyMetricKey, AnomalyMetricSemantics>,
): Map<string, MergedContributor> => {
  const map = new Map<string, MergedContributor>();

  for (const { incident, metric, unit } of metricIncidents) {
    const prompts = sortAnomalyPrompts(
      incident.topContributorPrompts,
      metricSemantics[metric].increaseIsPositive,
      incident.direction,
    );
    for (const prompt of prompts) {
      const key = String(prompt.monitorId);
      const existing = map.get(key) ?? { key, label: prompt.prompt, impacts: [] };
      existing.impacts.push({
        metric,
        type: prompt.type,
        value: prompt.value,
        direction: incident.direction,
        unit,
      });
      map.set(key, existing);
    }
  }

  return map;
};

const buildModelMap = (
  metricIncidents: MarkerMetricIncident[],
  metricSemantics: Record<AnomalyMetricKey, AnomalyMetricSemantics>,
): Map<string, MergedContributor> => {
  const map = new Map<string, MergedContributor>();

  for (const { incident, metric, unit } of metricIncidents) {
    const models = sortAnomalyModels(
      incident.topContributorModels,
      metricSemantics[metric].increaseIsPositive,
      incident.direction,
    );
    for (const model of models) {
      const key = model.model;
      const existing = map.get(key) ?? { key, label: getAnomalyModelLabel(model.model), impacts: [] };
      existing.impacts.push({
        metric,
        type: model.type,
        value: model.value,
        direction: incident.direction,
        unit,
      });
      map.set(key, existing);
    }
  }

  return map;
};

export const mergeAnomalyContributors = (
  metricIncidents: MarkerMetricIncident[],
  metricSemantics: Record<AnomalyMetricKey, AnomalyMetricSemantics>,
): { prompts: MergedContributor[]; models: MergedContributor[] } => {
  return {
    prompts: sortAndLimit(
      Array.from(buildPromptMap(metricIncidents, metricSemantics).values()),
      MAX_MERGED_PROMPTS,
    ),
    models: sortAndLimit(
      Array.from(buildModelMap(metricIncidents, metricSemantics).values()),
      MAX_MERGED_MODELS,
    ),
  };
};
